import {ApiClient} from '@hconnect/apiclient'
import {StoppageKind} from '@hconnect/common/types'
import {DateRange} from '@hconnect/uikit'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useEffect} from 'react'

import {DowntimeStoppage} from '../types'
import {prepareAggregatedStoppageCodePayload} from '../utils'

const QueryKey = 'plant-stoppages-details'

type Options<TSelect> = Omit<
  UseQueryOptions<DowntimeStoppage[], AxiosError, TSelect>,
  'queryKey' | 'queryFn'
> & {
  onError?: (error: AxiosError) => void
}

export const usePlantStoppagesDetails = <TSelect = DowntimeStoppage[]>(
  plantId: string,
  payload: DateRange<Date> & {
    selectedStoppedMainEquipments?: string[]
    selectedMainEquipments?: string[]
    selectedEquipments?: string[]
    selectedStoppages?: StoppageKind[]
    selectedStoppageCodes?: string[]
  },
  options?: Options<TSelect>
) => {
  const query = useQuery<DowntimeStoppage[], AxiosError, TSelect>({
    queryKey: [QueryKey, plantId, payload],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.get<DowntimeStoppage[]>(
        `/downtime-dashboard/${plantId}/stoppages`,
        {
          params: prepareAggregatedStoppageCodePayload({
            to: payload.to,
            from: payload.from,
            stoppedDepartmentUpmIds: payload.selectedStoppedMainEquipments,
            mainEquipmentNumbers: payload.selectedMainEquipments,
            equipmentNumbers: payload.selectedEquipments,
            kinds: payload.selectedStoppages,
            stoppageCodes: payload.selectedStoppageCodes
          }),
          signal
        }
      )

      return response.data
    },
    staleTime: 1000 * 60 * 15,
    ...options
  })

  const onError = options?.onError
  useEffect(() => {
    if (query.error) {
      onError?.(query.error)
    }
  }, [query.error, onError])

  return query
}
